import { useEffect, useState } from 'react';

import { api } from '../api/context';

const STEP = [
  {
    name: 'email',
    label: 'Confirm Your Email',
    path: '/wizard/email',
    button: 'Confirm Email',
  },
  {
    name: 'phone',
    label: 'Confirm Your Phone',
    path: '/wizard/phone',
    button: 'Complete Registration',
  },
  {
    name: 'profile',
    label: 'Complete Your Profile',
    path: '/wizard/profile',
    button: 'Complete Profile',
  },
  {
    name: 'socials',
    label: 'Connect Your TikTok',
    path: '/wizard/socials',
    button: 'Connect TikTok',
  },
];

const ORDER = ['phone', 'profile', 'socials'];

const isPhoneComplete = (profile) => (
  !!(profile && profile.phone_verified)
);

const isProfileComplete = (profile) => (
  !!(
    profile &&
    profile.first_name &&
    profile.last_name &&
    profile.gender &&
    profile.gender !== 'U'
  )
);

const isSocialsComplete = (socials) => {
  const tiktok = socials && socials.tiktok;
  return !!(tiktok && tiktok.uid);
};

const computeMissing = ({ profile, socials }) => {
  const missing = [];

  if (!isPhoneComplete(profile)) {
    missing.push('phone');
  }

  if (!isProfileComplete(profile)) {
    missing.push('profile');
  }

  if (!isSocialsComplete(socials)) {
    missing.push('socials');
  }

  return missing;
};

const stepFor = (name) => STEP.find(item => item.name === name);

const stepsForNames = (names) => (
  STEP.filter(item => names.includes(item.name))
);

const nextAfter = (current, missing) => {

  const idx = ORDER.indexOf(current);

  for (let i = idx + 1; i < ORDER.length; i++) {
    if (missing.includes(ORDER[i])) {
      return stepFor(ORDER[i]).path;
    }
  }

  return '/campaigns';
};

const fetchStatus = (token) => {
  return Promise.all([
    api(token).v1.profile.query()
      .then(res => (res && res.body && res.body.data) || null)
      .catch(() => null),
    api(token).v1.socials.query()
      .then(res => (res && res.body && res.body.data) || null)
      .catch(() => null),
  ]).then(([profile, socials]) => ({
    profile,
    socials,
    missing: computeMissing({ profile, socials }),
  }));
};

const useWizard = (token) => {

  const [ loading, setLoading ] = useState(true);
  const [ profile, setProfile ] = useState(null);
  const [ socials, setSocials ] = useState(null);

  useEffect(() => {

    if (!token) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetchStatus(token)
      .then(({ profile, socials }) => {
        if (cancelled) {
          return;
        }
        setProfile(profile);
        setSocials(socials);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };

  }, [token]);

  const ready = !loading && profile && socials;
  const missing = ready ? computeMissing({ profile, socials }) : [];

  return {
    loading,
    ready,
    profile,
    socials,
    missing,
  };
};

const POST_LOGIN_KEY = 'wizard_post_login';
const SIGNUP_KEY = 'wizard_signup';

const markPostLogin = () => {
  try {
    window.sessionStorage.setItem(POST_LOGIN_KEY, '1');
  } catch (err) {}
};

const consumePostLogin = () => {
  try {
    const value = window.sessionStorage.getItem(POST_LOGIN_KEY);
    if (value) {
      window.sessionStorage.removeItem(POST_LOGIN_KEY);
      return true;
    }
  } catch (err) {}
  return false;
};

const markSignup = () => {
  try {
    window.sessionStorage.setItem(SIGNUP_KEY, '1');
  } catch (err) {}
};

const isSignup = () => {
  try {
    return window.sessionStorage.getItem(SIGNUP_KEY) === '1';
  } catch (err) {}
  return false;
};

const clearSignup = () => {
  try {
    window.sessionStorage.removeItem(SIGNUP_KEY);
  } catch (err) {}
};

const wizard = {
  STEP,
  ORDER,

  computeMissing,
  fetchStatus,
  stepFor,
  stepsForNames,
  nextAfter,

  markPostLogin,
  consumePostLogin,

  markSignup,
  isSignup,
  clearSignup,

  useWizard,
};

export default wizard;
